import type { LngLat } from "./types.ts";
import type { Insets } from "./rotated-fit.ts";
import { fitRotated } from "./rotated-fit.ts";

/** Photo circle diameter for spot DOM markers (lng/lat anchor at center). */
export const QUEST_SPOT_DOM_PHOTO_PX = 40;

/** Uniform fit padding for spot-marker maps (photo anchor at lng/lat). */
export const QUEST_SPOT_DOM_FIT_PADDING_PX = 32;
export const QUEST_SPOT_DOM_SINGLE_ZOOM = 15;
/** Cap how far fit-all zooms in (urban multi-spot maps). */
export const QUEST_SPOT_DOM_FIT_MAX_ZOOM = 17;

/** Title sits to the right of the photo circle (gap + max title width on quest panel maps). */
export const QUEST_SPOT_DOM_TITLE_GAP_PX = 6;
export const QUEST_SPOT_DOM_TITLE_MAX_PX = 160;
/** Quest panel map uses a narrower title column; include both in symmetric padding. */
export const QUEST_SPOT_DOM_TITLE_MAX_PX_PANEL = 96;

const TITLE_RIGHT_OF_PHOTO_PX = QUEST_SPOT_DOM_TITLE_GAP_PX + QUEST_SPOT_DOM_TITLE_MAX_PX;

export type SpotFitPin = { lng: number; lat: number; kind?: string | null; title?: string | null };

/** Padding for fitRotated: 48px inset plus half the photo marker (lng/lat at photo center). */
export function questSpotDomMapFitPadding(base: Insets): Insets {
  const pad = QUEST_SPOT_DOM_FIT_PADDING_PX + QUEST_SPOT_DOM_PHOTO_PX / 2;
  return {
    top: Math.max(base.top, pad),
    right: Math.max(base.right, pad),
    bottom: Math.max(base.bottom, pad),
    left: Math.max(base.left, pad),
  };
}

/** Extra right inset so title labels east of the photo stay on screen after bounds fit. */
export function questSpotDomMapFitPaddingWithTitles(base: Insets): Insets {
  const uniform = questSpotDomMapFitPadding(base);
  const pad = QUEST_SPOT_DOM_FIT_PADDING_PX + QUEST_SPOT_DOM_PHOTO_PX / 2;
  return {
    ...uniform,
    right: Math.max(uniform.right, pad + TITLE_RIGHT_OF_PHOTO_PX),
  };
}

/**
 * Symmetric padding for spot fit: marker photo radius, base inset, and half of the widest
 * title column on each side so the lng/lat group centers in the map without toolbar skew.
 */
export function questSpotDomMapFitSymmetricPadding(base: Insets, width: number, height: number): Insets {
  const halfPhoto = QUEST_SPOT_DOM_PHOTO_PX / 2;
  const halfTitle = (QUEST_SPOT_DOM_TITLE_GAP_PX + Math.max(QUEST_SPOT_DOM_TITLE_MAX_PX, QUEST_SPOT_DOM_TITLE_MAX_PX_PANEL)) / 2;
  const markerPad = QUEST_SPOT_DOM_FIT_PADDING_PX + halfPhoto + halfTitle;
  const symmetric: Insets = {
    top: Math.max(base.top, base.bottom, markerPad),
    right: Math.max(base.left, base.right, markerPad),
    bottom: Math.max(base.top, base.bottom, markerPad),
    left: Math.max(base.left, base.right, markerPad),
  };
  return clampMapFitInsets(symmetric, width, height);
}

/** Checkpoints that contribute to spot-quest map framing (finite coords, not 0,0). */
export function spotQuestMapFitLngLats(pins: readonly SpotFitPin[] | null | undefined): LngLat[] {
  if (!pins?.length) return [];
  const out: LngLat[] = [];
  for (const p of pins) {
    if (p.kind === "start" || p.kind === "finish") continue;
    const lng = p.lng;
    const lat = p.lat;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    if (lng === 0 && lat === 0) continue;
    out.push([lng, lat]);
  }
  return out;
}

/** Keep toolbar/title padding from swallowing the whole map (MapLibre fit fallback). */
export function clampMapFitInsets(insets: Insets, width: number, height: number): Insets {
  if (width <= 0 || height <= 0) return insets;
  const minInner = 32;
  const maxSideX = Math.max(0, (width - minInner) / 2);
  const maxSideY = Math.max(0, (height - minInner) / 2);
  return {
    top: Math.min(insets.top, maxSideY),
    right: Math.min(insets.right, maxSideX),
    bottom: Math.min(insets.bottom, maxSideY),
    left: Math.min(insets.left, maxSideX),
  };
}

export function spotQuestMapFitPadding(base: Insets, width: number, height: number): Insets {
  const photoPad = QUEST_SPOT_DOM_FIT_PADDING_PX + QUEST_SPOT_DOM_PHOTO_PX / 2;
  const titleRight = photoPad + QUEST_SPOT_DOM_TITLE_GAP_PX + QUEST_SPOT_DOM_TITLE_MAX_PX_PANEL;
  return clampMapFitInsets(
    {
      top: Math.max(base.top, photoPad),
      bottom: Math.max(base.bottom, photoPad),
      left: Math.max(base.left, photoPad),
      right: Math.max(base.right, titleRight),
    },
    width,
    height,
  );
}

export interface SpotQuestMapFitView {
  center: LngLat;
  zoom: number;
  bearing: number;
}

/** Camera that fits all spot lng/lat with title-aware padding and maxZoom 16. */
export function spotQuestMapFitView(
  pins: readonly SpotFitPin[] | null | undefined,
  bearing: number,
  width: number,
  height: number,
  baseInsets: Insets,
): SpotQuestMapFitView | null {
  const points = spotQuestMapFitLngLats(pins);
  if (!points.length || width <= 0 || height <= 0) return null;
  if (points.length === 1) {
    return { center: points[0]!, zoom: QUEST_SPOT_DOM_SINGLE_ZOOM, bearing };
  }
  const padding = spotQuestMapFitPadding(baseInsets, width, height);
  return fitRotated(points, bearing, {
    width,
    height,
    padding,
    maxZoom: QUEST_SPOT_DOM_FIT_MAX_ZOOM,
  });
}
